import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ExperienceCertificatePreview from '../components/ExperienceCertificatePreview';

const ExperienceDashboard: React.FC = () => {
  const [nurses, setNurses] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedNurseId, setSelectedNurseId] = useState<number | null>(null);

  const fetchNurses = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const API_BASE_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";
      const response = await axios.get(`${API_BASE_URL}/api/nurses/`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      setNurses(response.data);
    } catch (error) {
      console.error('Failed to fetch nurses:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchNurses(); }, []);

  // Filter and Sort logic
  const filteredNurses = nurses
    .filter(nurse => 
      nurse.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      nurse.emp_id?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));

  // Helper to open the saved PDF in a new tab
  const handleViewPDF = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div style={{ padding: '2rem', backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ margin: 0, fontSize: '1.85rem', color: '#0f172a', fontWeight: 700 }}>Experience Certificate Generator</h1>
      </div>

      <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <div style={{ padding: '1.5rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between' }}>
          <h2 style={{ margin: 0, fontSize: '1.25rem' }}>Staff Directory</h2>
          <input 
            type="text" 
            placeholder="Search name or ID..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: '320px', padding: '0.65rem 1rem', borderRadius: '8px', border: '1px solid #cbd5e1' }}
          />
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'left', backgroundColor: '#f8fafc' }}>
              <th style={{ padding: '1rem 1.5rem' }}>Employee</th>
              <th style={{ padding: '1rem 1.5rem' }}>Status</th>
              <th style={{ padding: '1rem 1.5rem', textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
               <tr><td colSpan={3} style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>Loading staff data...</td></tr>
            ) : filteredNurses.length === 0 ? (
               <tr><td colSpan={3} style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>No staff found.</td></tr>
            ) : (
              filteredNurses.map((nurse) => (
                <tr key={nurse.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '1rem 1.5rem' }}>
                    <div style={{ fontWeight: 600 }}>{nurse.full_name}</div>
                    <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{nurse.emp_id}</div>
                  </td>
                  
                  {/* --- UPDATED: Status Column --- */}
                  <td style={{ padding: '1rem 1.5rem' }}>
                    {nurse.exp_certificate_url ? 
                      <span style={{ padding: '4px 8px', backgroundColor: '#dcfce7', color: '#166534', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 700 }}>GENERATED</span> :
                      <span style={{ padding: '4px 8px', backgroundColor: '#f1f5f9', color: '#64748b', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600 }}>PENDING</span>
                    }
                  </td>
                  
                  <td style={{ padding: '1rem 1.5rem', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                      
                      {/* Show View PDF button only if the URL exists in the database */}
                      {nurse.exp_certificate_url && (
                        <button 
                          onClick={() => handleViewPDF(nurse.exp_certificate_url)}
                          style={{ backgroundColor: '#f8fafc', color: '#334155', border: '1px solid #cbd5e1', padding: '0.5rem 1rem', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, transition: 'all 0.2s' }}
                          onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                          onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#f8fafc'; }}
                        >
                          📄 View PDF
                        </button>
                      )}

                      <button 
                        onClick={() => setSelectedNurseId(nurse.id)}
                        style={{ backgroundColor: '#eff6ff', color: '#2563eb', border: 'none', padding: '0.5rem 1rem', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, transition: 'all 0.2s' }}
                        onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#dbeafe'; }}
                        onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#eff6ff'; }}
                      >
                        {/* --- UPDATED: Dynamic Button Text --- */}
                        {nurse.exp_certificate_url ? '✏️ Regenerate' : '➕ Generate'}
                      </button>
                    </div>
                  </td>

                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {selectedNurseId && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }}>
            <ExperienceCertificatePreview 
              nurseId={selectedNurseId} 
              onClose={() => {
                setSelectedNurseId(null);
                fetchNurses(); // Refresh list to update status and fetch new PDF URL
              }} 
            />
        </div>
      )}
    </div>
  );
};

export default ExperienceDashboard;